import type { FlagDetail, FlagOverride, FlagScope, StoreFlagDefinition, StoredValue } from "../services/flag-store.service";

export const SCOPE_LABELS: Record<FlagScope, string> = {
  global: "Everyone",
  org: "Organization",
  classroom: "Classroom",
  user: "User",
};

export const SCOPE_ORDER: FlagScope[] = ["user", "classroom", "org", "global"];

export type KindFilter = "all" | StoreFlagDefinition["kind"];

export const KIND_LABELS: Record<KindFilter, string> = {
  all: "All",
  permission: "Permission",
  release: "Release",
  config: "Config",
  ops: "Ops",
};

export function kindCounts(flags: StoreFlagDefinition[]): [KindFilter, number][] {
  const order: KindFilter[] = ["permission", "release", "config", "ops"];
  const counts = order.map(kind => [kind, flags.filter(flag => flag.kind === kind).length] as [KindFilter, number]);
  return [["all", flags.length], ...counts.filter(([, count]) => count > 0)];
}

export function filterFlags(flags: StoreFlagDefinition[], kind: KindFilter, search: string): StoreFlagDefinition[] {
  const term = search.trim().toLowerCase();
  return flags.filter(flag =>
    (kind === "all" || flag.kind === kind) &&
    (!term || flag.key.toLowerCase().includes(term) || flag.description.toLowerCase().includes(term)));
}

export function formatStoredValue(value: StoredValue | null | undefined): string {
  if (value === null || value === undefined) return "Inherit";
  if (typeof value === "boolean") return value ? "On" : "Off";
  return String(value);
}

export function parseDraft(definition: StoreFlagDefinition, draft: string): StoredValue | string {
  if (definition.type === "bool") {
    if (draft === "true") return true;
    if (draft === "false") return false;
    return "Choose On or Off";
  }
  if (definition.type === "int") {
    if (!/^-?[0-9]+$/.test(draft.trim())) return "Enter a whole number";
    const value = Number(draft);
    if (definition.min !== null && value < definition.min) return `Must be at least ${definition.min}`;
    if (definition.max !== null && value > definition.max) return `Must be at most ${definition.max}`;
    return value;
  }
  if (definition.type === "enum") {
    return definition.values?.includes(draft) ? draft : "Choose a value";
  }
  return draft.trim() ? draft : "Enter a value";
}

export function isValidValue(definition: StoreFlagDefinition, parsed: StoredValue | string): parsed is StoredValue {
  if (definition.type === "bool") return typeof parsed === "boolean";
  if (definition.type === "int") return typeof parsed === "number";
  if (definition.type === "enum") return typeof parsed === "string" && !!definition.values?.includes(parsed);
  return typeof parsed === "string" && parsed.trim() !== "";
}

export function findOverride(detail: FlagDetail | null, scope: FlagScope, scopeId: string | null): FlagOverride | null {
  if (!detail) return null;
  const id = scope === "global" ? null : scopeId;
  return detail.overrides[scope]?.find(item => (item.scope_id ?? null) === id) ?? null;
}

export function overrideCount(detail: FlagDetail | null): number {
  if (!detail) return 0;
  return SCOPE_ORDER.reduce((total, scope) => total + (detail.overrides[scope]?.length ?? 0), 0);
}

export function mergeDetailPages(pages: FlagDetail[]): FlagDetail {
  const [first, ...rest] = pages;
  if (!first) throw new Error("no pages to merge");
  const overrides = { ...first.overrides };
  for (const page of rest) {
    for (const scope of Object.keys(page.overrides) as FlagScope[]) {
      overrides[scope] = [...(overrides[scope] ?? []), ...page.overrides[scope]];
    }
  }
  return { definition: first.definition, overrides, next_cursor: pages[pages.length - 1]!.next_cursor };
}

